/* V218.9 — Public "Connect Site" form for SEO Indexer Agent.
 *
 * Standalone landing/conversion page at /agents/seo-indexer/connect.
 * Anyone can paste their sitemap URL + email → get an API key in 30 seconds.
 *
 * Designed as the "tripwire" CTA from the marketing landing page.
 */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Copy, Loader2, Sparkles, AlertCircle } from "lucide-react";
import axios from "axios";
import SeoIndexerExitIntent from "../components/common/SeoIndexerExitIntent";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SeoIndexerConnectPage() {
  const [sitemap, setSitemap] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!sitemap.trim() || !email.trim()) {
      setErr("Vyplň sitemap URL a email.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/seo-indexer/tenants`, {
        sitemap_url: sitemap.trim(),
        owner_email: email.trim(),
        tenant_name: name.trim() || undefined,
      });
      setResult(r.data);
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      setErr(
        detail === "must_be_sitemap_xml"
          ? "URL musí byť tvoj sitemap.xml (nie iba homepage)."
          : detail === "invalid_sitemap_url"
            ? "Neplatná URL — skontroluj že začína https://"
            : detail || "Niečo sa pokazilo. Skús to znova alebo nás kontaktuj."
      );
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white py-16 px-4">
      {/* V218.10 — exit-intent free SEO audit capture (only shown if user hasn't submitted) */}
      {!result && <SeoIndexerExitIntent />}
      <Helmet>
        <title>Connect Your Site — SEO Indexer Agent · TopRankPulse</title>
        <meta
          name="description"
          content="Pripoj svoj sitemap za 30 sekúnd. SEO Indexer Agent ho pushne každú hodinu do Bing, Yandex, Seznam a Naver cez IndexNow. €49/mes alebo skús zadarmo 14 dní."
        />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-cyan-400 text-xs font-mono tracking-[0.3em] uppercase mb-3">
            🔎 SEO Indexer Agent
          </p>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-4">
            Pripoj svoj sitemap.<br />
            <span className="text-cyan-400">Indexovaný do hodiny.</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Tvoj agent bude každú hodinu sledovať tvoj sitemap.xml a pushovať nové URL-ky do
            Bing, Yandex, Seznam.cz a Naver cez IndexNow protokol.
          </p>
        </div>

        {!result ? (
          <form
            onSubmit={submit}
            data-testid="seo-indexer-connect-form"
            className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 sm:p-8 space-y-5 backdrop-blur"
          >
            <div>
              <label className="block text-xs font-mono tracking-wider text-zinc-400 uppercase mb-2">
                Sitemap URL <span className="text-rose-400">*</span>
              </label>
              <input
                type="url"
                data-testid="connect-sitemap-input"
                value={sitemap}
                onChange={(e) => setSitemap(e.target.value)}
                placeholder="https://your-shop.com/sitemap.xml"
                className="w-full px-4 py-3 bg-black/40 border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-cyan-500 focus:outline-none font-mono text-sm"
                required
              />
              <p className="text-zinc-500 text-xs mt-1">Musí byť priamy odkaz na sitemap.xml, nie homepage.</p>
            </div>

            <div>
              <label className="block text-xs font-mono tracking-wider text-zinc-400 uppercase mb-2">
                Email <span className="text-rose-400">*</span>
              </label>
              <input
                type="email"
                data-testid="connect-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@your-shop.com"
                className="w-full px-4 py-3 bg-black/40 border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-cyan-500 focus:outline-none"
                required
              />
              <p className="text-zinc-500 text-xs mt-1">Pošleme ti tvoj API key + anomaly alerty na túto adresu.</p>
            </div>

            <div>
              <label className="block text-xs font-mono tracking-wider text-zinc-400 uppercase mb-2">
                Názov projektu <span className="text-zinc-600">(voliteľné)</span>
              </label>
              <input
                type="text"
                data-testid="connect-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Cool Shop"
                className="w-full px-4 py-3 bg-black/40 border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-cyan-500 focus:outline-none"
              />
            </div>

            {err && (
              <div className="bg-rose-950/40 border border-rose-700/60 text-rose-300 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="connect-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="connect-submit-btn"
              disabled={loading}
              className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-black font-black uppercase tracking-wider text-sm py-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Registrujem..." : "Pripojiť sitemap · 14 dní zadarmo"}
            </button>

            <p className="text-zinc-500 text-xs text-center pt-2">
              Bez karty. Bez záväzkov. Po 14 dňoch €49/mes alebo zruš jedným curl-om.
            </p>
          </form>
        ) : (
          <div
            data-testid="connect-success"
            className="bg-emerald-950/30 border border-emerald-600/40 rounded-2xl p-6 sm:p-8 space-y-5"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              <h2 className="text-2xl font-black text-emerald-300">Agent je aktívny ✨</h2>
            </div>

            <p className="text-zinc-300">
              Pre <strong className="text-white">{result.tenant_name || result.host}</strong>{" "}
              budem každú hodinu pushovať nové URL-ky. Welcome email s detailmi je na ceste na{" "}
              <strong className="text-cyan-300">{result.owner_email}</strong>.
            </p>

            <div className="bg-black/40 border border-cyan-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono tracking-wider text-cyan-400 uppercase">Tvoj API key</span>
                <button
                  type="button"
                  onClick={() => copy(result.api_key)}
                  data-testid="connect-copy-key"
                  className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  {copied ? "Skopírované!" : "Kopírovať"}
                </button>
              </div>
              <code className="text-cyan-300 text-sm break-all block" data-testid="connect-api-key">{result.api_key}</code>
              <p className="text-rose-400 text-xs mt-3 font-bold">⚠ Drž v tajnosti — toto je tvoj jediný secret.</p>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-bold text-white">3 veci čo môžeš spraviť teraz</h3>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                <p className="text-xs font-mono tracking-wider text-zinc-400 uppercase mb-1">Force push (teraz, nečakaj 1h)</p>
                <code className="text-emerald-300 text-xs break-all">
                  curl -X POST https://toprankpulse.com/api/seo-indexer/tenants/{result.api_key}/trigger
                </code>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                <p className="text-xs font-mono tracking-wider text-zinc-400 uppercase mb-1">Status</p>
                <code className="text-emerald-300 text-xs break-all">
                  curl https://toprankpulse.com/api/seo-indexer/tenants/{result.api_key}/status
                </code>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                <p className="text-xs font-mono tracking-wider text-zinc-400 uppercase mb-1">Pause cron</p>
                <code className="text-emerald-300 text-xs break-all">
                  curl -X POST https://toprankpulse.com/api/seo-indexer/tenants/{result.api_key}/disable
                </code>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl mb-2">⚡</div>
            <div className="font-bold mb-1">Diff, nie spam</div>
            <p className="text-zinc-500 text-xs">Engines vidia iba nové URL od posledného behu.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl mb-2">🛡️</div>
            <div className="font-bold mb-1">Anomaly alerty</div>
            <p className="text-zinc-500 text-xs">Email keď je sitemap rozbitý alebo engines down.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl mb-2">🇪🇺</div>
            <div className="font-bold mb-1">EU · GDPR</div>
            <p className="text-zinc-500 text-xs">Dáta zostávajú v Európe, žiadne tracking pixely.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
